import React, { useState } from "react";
import {
  Download,
  CheckCircle2,
  Sparkles,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

const STEPS = [
  {
    number: "01",
    title: "Collect",
    description:
      "Capture emails from forms, landing pages, CRMs, and imports.",
    icon: Download,
    color: "sky",
    features: [
      "Web forms & landing pages",
      "CRM imports",
      "CSV uploads",
      "API integrations",
    ],
  },
  {
    number: "02",
    title: "Validate",
    description:
      "VALIDMAIL checks syntax, domain, mailbox, role addresses, disposable and spam traps.",
    icon: Sparkles,
    color: "blue",
    features: [
      "Syntax verification",
      "Domain validation",
      "Mailbox existence",
      "Spam trap detection",
    ],
  },
  {
    number: "03",
    title: "Clean",
    description:
      "Remove risk: invalid, disposable, abuse, catch-all, and unknowns per your policy.",
    icon: CheckCircle2,
    color: "violet",
    features: [
      "Remove invalid emails",
      "Filter disposables",
      "Flag risky addresses",
      "Custom rules",
    ],
  },
  {
    number: "04",
    title: "Sync",
    description:
      "Push clean lists and statuses back to your ESP, CRM, or product in real time.",
    icon: RefreshCw,
    color: "indigo",
    features: [
      "ESP sync",
      "CRM updates",
      "Webhooks",
      "Real-time API",
    ],
  },
];

const colorClasses = {
  sky: {
    bg: "bg-sky-50 dark:bg-sky-900/20",
    border: "border-sky-200 dark:border-sky-800",
    text: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-500",
    gradient: "from-sky-500 to-blue-600",
  },
  blue: {
    bg: "bg-blue-50 dark:bg-blue-900/20",
    border: "border-blue-200 dark:border-blue-800",
    text: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-500",
    gradient: "from-blue-500 to-violet-600",
  },
  violet: {
    bg: "bg-violet-50 dark:bg-violet-900/20",
    border: "border-violet-200 dark:border-violet-800",
    text: "text-violet-600 dark:text-violet-400",
    iconBg: "bg-violet-500",
    gradient: "from-violet-500 to-purple-600",
  },
  indigo: {
    bg: "bg-indigo-50 dark:bg-indigo-900/20",
    border: "border-indigo-200 dark:border-indigo-800",
    text: "text-indigo-600 dark:text-indigo-400",
    iconBg: "bg-indigo-500",
    gradient: "from-indigo-500 to-blue-600",
  },
};

export default function HowItWorksSection({ onSeeAction }) {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section className="py-16 lg:py-24 bg-white dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Headline */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            How VALIDMAIL fits into your{" "}
            <span className="text-sky-600 dark:text-sky-400">
              email workflow
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            A simple 4-step process that integrates seamlessly with your
            existing tools and workflows
          </p>
        </div>

        {/* Desktop: Horizontal Flow */}
        <div className="hidden lg:block mb-16">
          <div className="relative">
            {/* Connection lines */}
            <div className="absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 via-blue-200 via-violet-200 to-indigo-200 dark:from-sky-800 dark:via-blue-800 dark:via-violet-800 dark:to-indigo-800" />

            <div className="grid grid-cols-4 gap-8">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const colors = colorClasses[step.color];
                const isActive = activeStep === index;

                return (
                  <div
                    key={index}
                    className="relative"
                    onMouseEnter={() => setActiveStep(index)}
                  >
                    {/* Step Card */}
                    <div
                      className={`relative bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 transition-all duration-300 ${
                        isActive
                          ? `${colors.border} shadow-xl scale-105`
                          : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`w-16 h-16 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center mb-4 mx-auto shadow-lg`}
                      >
                        <Icon className="h-8 w-8 text-white" />
                      </div>

                      {/* Number */}
                      <div
                        className={`text-sm font-bold ${colors.text} mb-2 text-center`}
                      >
                        STEP {step.number}
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-3 text-center">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 text-center leading-relaxed">
                        {step.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-2">
                        {step.features.map((feature, fIndex) => (
                          <li
                            key={fIndex}
                            className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
                          >
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${colors.iconBg}`}
                            />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {/* Arrow connector (not on last item) */}
                      {index < STEPS.length - 1 && (
                        <div className="absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                          <ArrowRight
                            className={`h-8 w-8 ${colors.text} transition-all ${
                              isActive ? "scale-125" : ""
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile & Tablet: Vertical Flow */}
        <div className="lg:hidden mb-12">
          <div className="space-y-6">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const colors = colorClasses[step.color];

              return (
                <div
                  key={index}
                  className={`relative bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 ${colors.border}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div
                      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}
                    >
                      <Icon className="h-7 w-7 text-white" />
                    </div>

                    <div className="flex-1">
                      {/* Number */}
                      <div
                        className={`text-xs font-bold ${colors.text} mb-1`}
                      >
                        STEP {step.number}
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-3 leading-relaxed">
                        {step.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-1.5">
                        {step.features.map((feature, fIndex) => (
                          <li
                            key={fIndex}
                            className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
                          >
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${colors.iconBg}`}
                            />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Down arrow (not on last item) */}
                  {index < STEPS.length - 1 && (
                    <div className="flex justify-center mt-4">
                      <ArrowRight
                        className={`h-6 w-6 ${colors.text} rotate-90`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={onSeeAction}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200 group"
          >
            See it in action
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
